import { useEffect, useRef, useState, useCallback } from "react";
import axios from "../../../config/axios";
import useBeaconControl from "../../../hooks/useBeaconControl";

/* ============================================================
   InteractAlertsTV — remote-driven sibling of InteractAlerts.
   Engine (standby warm loop, warm/cold sequences, abort) is
   byte-identical to the display file; only navigation + sizing
   differ. Keep engines in sync BY HAND (same rule as the timer
   schedules).
   ============================================================ */

const STORE_KEY = "waitsigns_ix_alerts";

const ALERT_COLORS = [
  { key: "green",  hex: "#30D158" },
  { key: "red",    hex: "#FF3B30" },
  { key: "blue",   hex: "#0A84FF" },
  { key: "pink",   hex: "#FF2D55" },
  { key: "purple", hex: "#BF5AF2" },
  { key: "orange", hex: "#FF9500" },
];
const FX = {
  green:  { circle: "CIRCLE_GREEN",  lightning: "LIGHTNING_GREEN" },
  red:    { circle: "CIRCLE_RED",    lightning: "LIGHTNING_RED" },
  blue:   { circle: "CIRCLE_BLUE",   lightning: "LIGHTNING_BLUE" },
  pink:   { circle: "CIRCLE_PINK",   lightning: "LIGHTNING_PINK" },
  purple: { circle: "CIRCLE_PURPLE", lightning: "LIGHTNING_PURPLE" },
  orange: { circle: "CIRCLE_ORANGE", lightning: "LIGHTNING_ORANGE" },
};

const PRESET_MEANINGS = [
  "Dinner Time",
  "Come Downstairs",
  "Kiss Goodnight",
  "Time to Leave",
  "Homework",
  "Clean Up",
  "Check Your Phone",
  "Love You",
  "Miss You",
  "Fireworks Starting!"
];

const DEFAULT_MEANINGS = {
  green:  "Fireworks Starting!",
  red:    "Love You",
  blue:   "Kiss Goodnight",
  pink:   "Dinner Time",
  purple: "Homework",
  orange: "Time to Leave",
};

const loadMeanings = () => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { ...DEFAULT_MEANINGS };
    return { ...DEFAULT_MEANINGS, ...JSON.parse(raw) };
  } catch { return { ...DEFAULT_MEANINGS }; }
};
const saveMeanings = (m) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(m)); } catch {}
};

const GAP_MS = 2000;
const STANDBY_FAST_MS = 5000;
const STANDBY_MAX_MS  = 60000;
const REPEAT_MS = 3000;
const SOLID_OFF_HOLD_MS = 5000;

/* ---- Raw transport ---- */
const postShowCmd = (deviceId, fx, vib) =>
  axios.post("/esp/beacon-show", { deviceId, fx, vib }, { withCredentials: true })
    .catch((err) => console.error("[AlertsTV] cmd failed:", fx, err));
const postCancelRaw = (deviceId) =>
  axios.post("/esp/cancel", { deviceId }, { withCredentials: true })
    .catch((err) => console.error("[AlertsTV] cancel failed:", err));

/* ---- Remote keys (BeaconDemo-proven set) ---- */
const OKK = (e) => e.key === "Enter" || e.key === " " || e.key === "OK" || e.key === "Select" ||
  e.keyCode === 13 || e.keyCode === 23 || e.keyCode === 66;
const BACKK = (e) => e.key === "Escape" || e.keyCode === 27 || e.keyCode === 461 || e.keyCode === 10009;

/* ---- Main focus grid: rows top→bottom ---- */
const NAV_ROWS = [
  ["edit"],
  ["t0", "t1", "t2"],
  ["t3", "t4", "t5"],
  ["back"],
];
const NAV_POS = {};
NAV_ROWS.forEach((row, r) => row.forEach((k, c) => { NAV_POS[k] = [r, c]; }));

/* ---- Picker focus grid (10 presets 2-col, custom input, cancel/save) ---- */
const PICK_ROWS = [
  ["m0", "m1"],
  ["m2", "m3"],
  ["m4", "m5"],
  ["m6", "m7"],
  ["m8", "m9"],
  ["custom"],
  ["cancel", "save"],
];
const PICK_POS = {};
PICK_ROWS.forEach((row, r) => row.forEach((k, c) => { PICK_POS[k] = [r, c]; }));

const IXAT_STYLE = `
  .ixat-alerts{display:flex;width:100%;height:100%;min-height:0;
    font-family:"Lato",sans-serif;color:#1a2542;position:relative;outline:none;}
  .ixat-panel{flex:1;background:#fff;border-radius:28px;padding:26px 32px;display:flex;
    flex-direction:column;box-shadow:0 10px 34px rgba(20,30,60,.08);min-height:0;
    overflow:hidden;position:relative;}
  .ixat-tag{align-self:flex-start;background:#007bff;color:#fff;font-size:22px;
    font-weight:900;letter-spacing:3px;padding:8px 18px;border-radius:8px;margin-bottom:14px;}
  .ixat-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;}
  .ixat-title{font-size:38px;font-weight:900;margin:0 0 4px;letter-spacing:-.5px;line-height:1.05;}
  .ixat-tagline{font-size:24px;font-weight:800;color:#007bff;margin:0 0 16px;}
  .ixat-edit-btn{flex:0 0 auto;height:70px;padding:0 34px;border-radius:16px;
    border:4px solid #d8dce3;background:#fff;color:#1a2542;font-family:"Lato",sans-serif;
    font-size:24px;font-weight:900;letter-spacing:1px;cursor:pointer;outline:none;
    transition:transform .12s ease, box-shadow .12s ease, background .12s ease, color .12s ease;}
  .ixat-edit-btn.on{background:#007bff;border-color:#007bff;color:#fff;}
  .ixat-edit-btn:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);
    transform:scale(1.03);}
  .ixat-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;flex:1;
    min-height:0;align-content:stretch;margin-bottom:14px;}
  .ixat-tile{position:relative;border-radius:18px;border:5px solid transparent;cursor:pointer;
    outline:none;display:flex;align-items:center;justify-content:center;
    color:#fff;font-family:"Lato",sans-serif;overflow:hidden;
    transition:transform .12s ease, box-shadow .12s ease;}
  .ixat-tile:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);
    transform:scale(1.03);z-index:1;}
  .ixat-tile-label{font-size:32px;font-weight:900;letter-spacing:.5px;text-align:center;
    padding:0 14px;line-height:1.15;text-shadow:0 1px 4px rgba(0,0,0,.35);}
  .ixat-tile-pencil{position:absolute;top:12px;right:14px;font-size:22px;
    text-shadow:0 1px 3px rgba(0,0,0,.4);}
  .ixat-actions{margin-top:auto;display:flex;gap:16px;}
  .ixat-btn-back{flex:0 0 210px;height:70px;border-radius:16px;border:4px solid #d8dce3;
    background:#fff;color:#1a2542;font-family:"Lato",sans-serif;font-size:27px;font-weight:900;
    letter-spacing:1px;cursor:pointer;outline:none;display:flex;align-items:center;
    justify-content:center;transition:transform .12s ease, box-shadow .12s ease;}
  .ixat-btn-back:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);
    transform:scale(1.03);}

  /* Sending overlay */
  .ixat-ov{position:absolute;inset:0;z-index:50;border-radius:28px;
    display:flex;flex-direction:column;align-items:center;justify-content:center;gap:22px;
    background:rgba(10,14,28,.86);text-align:center;padding:30px;}
  .ixat-ov-label{font-size:42px;font-weight:900;color:#fff;line-height:1.1;
    text-shadow:0 4px 24px rgba(0,0,0,.5);}
  .ixat-ov-phase{font-size:24px;font-weight:800;color:#cdd3e4;}
  .ixat-ov-count{font-size:84px;font-weight:900;color:#fff;line-height:1;
    font-variant-numeric:tabular-nums;}
  .ixat-ov-ring{width:170px;height:170px;border-radius:50%;
    border:14px solid rgba(255,255,255,.14);border-top-color:currentColor;
    animation:ixat-spin .9s linear infinite;
    filter:drop-shadow(0 0 14px currentColor);}
  @keyframes ixat-spin{to{transform:rotate(360deg);}}
  .ixat-ov-flash{width:170px;height:170px;border-radius:50%;background:currentColor;
    box-shadow:0 0 44px currentColor;animation:ixat-flash .5s ease-in-out infinite;}
  @keyframes ixat-flash{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.15;transform:scale(.92);}}
  .ixat-ov-cancel{padding:18px 52px;border-radius:999px;border:4px solid transparent;
    background:rgba(255,255,255,.14);color:#e6e6ee;font-family:"Lato",sans-serif;
    font-size:24px;font-weight:800;letter-spacing:1px;text-transform:uppercase;
    cursor:pointer;outline:none;transition:background .15s ease, transform .12s ease, box-shadow .12s ease;}
  .ixat-ov-cancel:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651;transform:scale(1.04);}
  .ixat-ov-done{color:#30D158;font-size:30px;font-weight:900;}

  /* Label picker (mbtv-modal look) */
  .ixat-pick-back{position:fixed;inset:0;background:rgba(0,0,0,.62);z-index:10000;
    display:flex;align-items:center;justify-content:center;}
  .ixat-pick{background:#fff;border-radius:20px;padding:36px 34px 30px;max-width:760px;
    width:calc(100% - 80px);font-family:"Lato",sans-serif;text-align:center;
    box-shadow:0 16px 50px rgba(0,0,0,.5);}
  .ixat-pick h3{margin:0 0 6px;font-size:36px;color:#1f2937;font-weight:900;}
  .ixat-pick-sub{margin:0 0 20px;font-size:20px;color:#6b7280;}
  .ixat-pick-list{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
  .ixat-pick-item{height:58px;border-radius:14px;border:4px solid #d8dce3;background:#fff;
    color:#1a2542;font-family:"Lato",sans-serif;font-size:20px;font-weight:800;cursor:pointer;
    outline:none;transition:transform .12s ease, border-color .12s ease, box-shadow .12s ease;}
  .ixat-pick-item.on{border-color:#007bff;background:#eaf2ff;}
  .ixat-pick-item:focus{border-color:#05f651;box-shadow:0 0 0 4px #05f651;transform:scale(1.03);
    z-index:1;position:relative;}
  .ixat-pick-custom{display:flex;gap:12px;margin-bottom:18px;}
  .ixat-pick-customBtn{flex:0 0 220px;}
  .ixat-pick-input{flex:1;height:58px;border-radius:14px;border:4px solid #d8dce3;
    padding:0 18px;font-family:"Lato",sans-serif;font-size:22px;font-weight:700;color:#1a2542;
    outline:none;}
  .ixat-pick-input:focus{border-color:#05f651;box-shadow:0 0 0 4px #05f651;}
  .ixat-pick-btns{display:flex;gap:14px;}
  .ixat-pick-cancel{flex:1;height:64px;border-radius:14px;border:4px solid transparent;
    background:#e5e7eb;color:#374151;font-family:"Lato",sans-serif;font-size:24px;font-weight:900;
    cursor:pointer;outline:none;transition:transform .12s ease, box-shadow .12s ease;}
  .ixat-pick-save{flex:1;height:64px;border-radius:14px;border:4px solid transparent;
    background:#007bff;color:#fff;font-family:"Lato",sans-serif;font-size:24px;font-weight:900;
    cursor:pointer;outline:none;transition:transform .12s ease, box-shadow .12s ease;}
  .ixat-pick-cancel:focus,.ixat-pick-save:focus{border-color:#05f651;
    box-shadow:0 0 0 4px #05f651;transform:scale(1.04);}

  /* Take-control modal */
  .ixat-modal-back{position:fixed;inset:0;background:rgba(0,0,0,.62);z-index:10000;
    display:flex;align-items:center;justify-content:center;}
  .ixat-modal{background:#fff;border-radius:20px;padding:44px 40px 36px;max-width:720px;
    width:calc(100% - 80px);text-align:center;font-family:"Lato",sans-serif;
    box-shadow:0 16px 50px rgba(0,0,0,.5);}
  .ixat-modal h3{margin:0 0 16px;font-size:40px;color:#1f2937;font-weight:900;}
  .ixat-modal p{margin:0 0 32px;font-size:26px;color:#4b5563;line-height:1.4;}
  .ixat-modal-btns{display:flex;gap:18px;justify-content:center;}
  .ixat-modal-btn{flex:1;padding:20px 28px;border-radius:14px;border:4px solid transparent;
    font-family:"Lato",sans-serif;font-size:28px;font-weight:900;cursor:pointer;outline:none;
    transition:transform .12s ease, box-shadow .12s ease;}
  .ixat-modal-btn:focus{border-color:#05f651;box-shadow:0 0 0 3px rgba(5,246,81,.4);transform:scale(1.06);}
  .ixat-modal-no{background:#e5e7eb;color:#374151;border-color:#9ca3af;}
  .ixat-modal-yes{background:#007bff;color:#fff;}
`;

const InteractAlertsTV = ({ onBack, onExitTop, device }) => {
  const [meanings, setMeanings] = useState(loadMeanings);
  const [editMode, setEditMode] = useState(false);
  const [picking, setPicking] = useState(null);
  const [pickSel, setPickSel] = useState("");
  const [pickCustom, setPickCustom] = useState("");
  const [sending, setSending] = useState(null);
  const [ovPhase, setOvPhase] = useState("idle");
  const [countLeft, setCountLeft] = useState(0);
  const [pendingControl, setPendingControl] = useState(false);
  const pendingColorRef = useRef(null);

  /* ---- Beacon control + gated senders (identical to display) ---- */
  const { isController, hasHolder, holder, claim } = useBeaconControl();
  const isControllerRef = useRef(isController);
  useEffect(() => { isControllerRef.current = isController; }, [isController]);
  const deviceId = device?.magic_beacon_device?._id ?? null;
  const deviceIdRef = useRef(deviceId);
  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);
  const lastCmdAtRef = useRef(0);
  const sentAnyRef = useRef(false);
  const canSend = useCallback(() => !!deviceIdRef.current && !!isControllerRef.current, []);
  const sendCmd = useCallback((fx, vib) => {
    if (!canSend()) { console.log(`[AlertsTV] SKIPPED fx=${fx}`); return Promise.resolve(); }
    console.log(`[AlertsTV] send fx=${fx} vib=${vib}`);
    sentAnyRef.current = true;
    lastCmdAtRef.current = Date.now();
    return postShowCmd(deviceIdRef.current, fx, vib);
  }, [canSend]);
  const sendCancel = useCallback(() => {
    if (!canSend()) { console.log("[AlertsTV] SKIPPED CANCEL"); return Promise.resolve(); }
    console.log("[AlertsTV] send CANCEL");
    sentAnyRef.current = true;
    lastCmdAtRef.current = Date.now();
    return postCancelRaw(deviceIdRef.current);
  }, [canSend]);

  /* ---- Timers + standby engine (identical to display) ---- */
  const seqTimersRef = useRef([]);
  const standbyTimersRef = useRef([]);
  const standbyLoopRef = useRef(null);
  const warmRef = useRef(false);
  const countdownIntRef = useRef(null);
  const circleAtRef = useRef(0);
  const colorStartedRef = useRef(false); // any CIRCLE/LIGHTNING sent this run
  const [windingDown, setWindingDown] = useState(false); // abort wind-down: grid locked

  const clearSeq = useCallback(() => {
    seqTimersRef.current.forEach(clearTimeout);
    seqTimersRef.current = [];
    if (countdownIntRef.current) { clearInterval(countdownIntRef.current); countdownIntRef.current = null; }
  }, []);
  const clearStandby = useCallback(() => {
    standbyTimersRef.current.forEach(clearTimeout);
    standbyTimersRef.current = [];
    if (standbyLoopRef.current) { clearInterval(standbyLoopRef.current); standbyLoopRef.current = null; }
    warmRef.current = false;
  }, []);

  const startStandby = useCallback(() => {
    clearStandby();
    sendCancel();
    const t1 = setTimeout(() => {
      warmRef.current = true;
      sendCmd("WAKE_UP_FAST", "OFF");
      standbyLoopRef.current = setInterval(() => sendCmd("WAKE_UP_FAST", "OFF"), STANDBY_FAST_MS);
    }, GAP_MS);
    const tCap = setTimeout(() => {
      console.log("[AlertsTV] standby cap hit — CANCEL + cold until next tap");
      if (standbyLoopRef.current) { clearInterval(standbyLoopRef.current); standbyLoopRef.current = null; }
      warmRef.current = false;
      sendCancel();
    }, STANDBY_MAX_MS);
    standbyTimersRef.current.push(t1, tCap);
  }, [clearStandby, sendCancel, sendCmd]);

  useEffect(() => {
    startStandby();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    clearSeq();
    clearStandby();
    if (sentAnyRef.current) sendCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSequence = (colorKey) => {
    const warm = warmRef.current;
    clearStandby();
    const c = ALERT_COLORS.find((x) => x.key === colorKey);
    setSending({ key: colorKey, hex: c.hex, label: meanings[colorKey] });

    const base = Math.max(0, lastCmdAtRef.current + GAP_MS - Date.now());
    const steps = [];
    let t = base;
    if (!warm) {
      steps.push([t, "CANCEL"]); t += GAP_MS;
      steps.push([t, "WAKE_UP_FAST", "OFF"]); t += GAP_MS;
      steps.push([t, "WAKE_UP_FAST", "OFF"]); t += GAP_MS;
      steps.push([t, "WAKE_UP_FAST", "OFF"]); t += GAP_MS;
    }
    steps.push([t, "CANCEL"]); t += GAP_MS;
    const circleAt = t;
    steps.push([t, FX[colorKey].circle, "OFF"]); t += REPEAT_MS;
    steps.push([t, FX[colorKey].circle, "OFF"]); t += REPEAT_MS;
    steps.push([t, "CANCEL"]); t += GAP_MS;
    const lightAt = t;
    steps.push([t, FX[colorKey].lightning, "PULSE"]); t += REPEAT_MS;
    steps.push([t, FX[colorKey].lightning, "PULSE"]); t += REPEAT_MS;
    steps.push([t, "CANCEL"]); t += GAP_MS;
    steps.push([t, "SOLID_OFF", "OFF"]);
    const endAt = t;

    console.log(`[AlertsTV] ${colorKey} ${warm ? "WARM" : "COLD"}: circle@${circleAt}ms light@${lightAt}ms end@${endAt}ms`);

    colorStartedRef.current = false;
    steps.forEach(([at, fx, vib]) => {
      const tm = setTimeout(() => {
        if (fx.startsWith("CIRCLE") || fx.startsWith("LIGHTNING")) colorStartedRef.current = true;
        fx === "CANCEL" ? sendCancel() : sendCmd(fx, vib);
      }, at);
      seqTimersRef.current.push(tm);
    });

    if (!warm) {
      setOvPhase("waking");
      circleAtRef.current = Date.now() + circleAt;
      setCountLeft(Math.ceil(circleAt / 1000));
      countdownIntRef.current = setInterval(() => {
        const left = Math.max(0, Math.ceil((circleAtRef.current - Date.now()) / 1000));
        setCountLeft(left);
      }, 250);
    } else {
      setOvPhase("circle");
    }
    const pC = setTimeout(() => {
      if (countdownIntRef.current) { clearInterval(countdownIntRef.current); countdownIntRef.current = null; }
      setOvPhase("circle");
    }, circleAt);
    const pL = setTimeout(() => setOvPhase("lightning"), lightAt);
    const pD = setTimeout(() => setOvPhase("done"), endAt);
    const pClose = setTimeout(() => {
      setSending(null);
      setOvPhase("idle");
    }, endAt + 2000);
    const pRestart = setTimeout(() => startStandby(), endAt + SOLID_OFF_HOLD_MS);
    seqTimersRef.current.push(pC, pL, pD, pClose, pRestart);
  };

  // Abort: pre-color → bare CANCEL (short lockout). Color already went out →
  // CANCEL → 2s → SOLID_OFF → hold. Grid is locked (windingDown) until done.
  const abortSequence = () => {
    clearSeq();
    setSending(null);
    setOvPhase("idle");
    setWindingDown(true); // grid locked until the wind-down finishes
    const hadColor = colorStartedRef.current;
    const wait = Math.max(0, lastCmdAtRef.current + GAP_MS - Date.now());
    const t1 = setTimeout(() => {
      sendCancel();
      if (!hadColor) {
        // Nothing but wakeups went out — a bare CANCEL is enough.
        setWindingDown(false);
        startStandby();
        return;
      }
      const t2 = setTimeout(() => {
        sendCmd("SOLID_OFF", "OFF");
        const t3 = setTimeout(() => {
          setWindingDown(false);
          startStandby();
        }, SOLID_OFF_HOLD_MS);
        seqTimersRef.current.push(t3);
      }, GAP_MS);
      seqTimersRef.current.push(t2);
    }, wait);
    seqTimersRef.current.push(t1);
  };

  const requestSend = async (colorKey) => {
    if (sending || windingDown) return;
    if (editMode) { openPicker(colorKey); return; }
    if (isController) { runSequence(colorKey); return; }
    if (hasHolder && holder?.holderDeviceId !== device?._id) {
      pendingColorRef.current = colorKey;
      setPendingControl(true);
      return;
    }
    const r = await claim();
    if (r?.ok) runSequence(colorKey);
    else console.error("[AlertsTV] claim failed:", r?.reason);
  };
  const confirmTakeControl = async () => {
    setPendingControl(false);
    const r = await claim();
    if (r?.ok) runSequence(pendingColorRef.current);
    else console.error("[AlertsTV] take-control claim failed:", r?.reason);
  };
  const cancelTakeControl = () => setPendingControl(false);

  /* ---- Label picker ---- */
  const openPicker = (colorKey) => {
    const cur = meanings[colorKey] || "";
    setPicking(colorKey);
    setPickSel(PRESET_MEANINGS.includes(cur) ? cur : "");
    setPickCustom(PRESET_MEANINGS.includes(cur) ? "" : cur);
  };
  const savePick = () => {
    const val = (pickCustom.trim() || pickSel || "").slice(0, 24);
    if (!val) { setPicking(null); return; }
    const next = { ...meanings, [picking]: val };
    setMeanings(next);
    saveMeanings(next);
    setPicking(null);
  };

  /* ---- TV: focus plumbing ---- */
  const btnRefs = useRef({});
  const setBtnRef = (key) => (el) => { btnRefs.current[key] = el; };
  const pickRefs = useRef({});
  const setPickRef = (key) => (el) => { pickRefs.current[key] = el; };
  const ovCancelRef = useRef(null);
  const inputRef = useRef(null);
  const pcNoRef = useRef(null);
  const pcYesRef = useRef(null);

  const focusKey = (key) => btnRefs.current[key]?.focus({ preventScroll: true });
  const focusPick = (key) => pickRefs.current[key]?.focus({ preventScroll: true });

  // Entry focus: first tile.
  useEffect(() => {
    const t = setTimeout(() => focusKey("t0"), 60);
    return () => clearTimeout(t);
  }, []);
  // Overlay open → Cancel; overlay closed → grid (EDIT while locked).
  useEffect(() => {
    if (sending && ovPhase !== "done") {
      const t = setTimeout(() => ovCancelRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    if (!sending) {
      const t = setTimeout(() => focusKey(windingDown ? "edit" : "t0"), 60);
      return () => clearTimeout(t);
    }
  }, [sending, ovPhase, windingDown]);
    // Picker open → first preset; closed → EDIT/DONE (they're mid-edit-mode,
  // so the natural landing after Save or Cancel is the DONE button).
  const pickingWasOpenRef = useRef(false);
  useEffect(() => {
    if (picking) {
      pickingWasOpenRef.current = true;
      const t = setTimeout(() => focusPick("m0"), 60);
      return () => clearTimeout(t);
    }
    if (pickingWasOpenRef.current) {
      pickingWasOpenRef.current = false;
      const t = setTimeout(() => focusKey("edit"), 60);
      return () => clearTimeout(t);
    }
  }, [picking]);
  useEffect(() => {
    if (pendingControl) {
      const t = setTimeout(() => pcYesRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [pendingControl]);

  const keyOf = (map, el) => {
    for (const [k, v] of Object.entries(map)) if (v === el) return k;
    return null;
  };

  const handleKey = (e) => {
    e.stopPropagation();

    // Sending overlay: OK = cancel/dismiss; BACKK = abort. Nothing else.
    if (sending) {
      e.preventDefault();
      if (ovPhase !== "done" && (OKK(e) || BACKK(e))) abortSequence();
      return;
    }

    if (BACKK(e)) { e.preventDefault(); onBack(); return; }

    const key = keyOf(btnRefs.current, e.target);
    if (OKK(e)) { e.preventDefault(); e.target?.click?.(); return; }
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
    if (!key) { e.preventDefault(); focusKey("t0"); return; }
    const [r, c] = NAV_POS[key];
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (c > 0) focusKey(NAV_ROWS[r][c - 1]);
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      if (c < NAV_ROWS[r].length - 1) focusKey(NAV_ROWS[r][c + 1]);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (r === 0) { onExitTop?.(); return; }
      const up = NAV_ROWS[r - 1];
      focusKey(up[Math.min(c, up.length - 1)]);
      return;
    }
    if (e.key === "ArrowDown") {
      if (r === NAV_ROWS.length - 1) return; // Back → bottom bar (native drop)
      e.preventDefault();
      const dn = NAV_ROWS[r + 1];
      focusKey(dn[Math.min(c, dn.length - 1)]);
      return;
    }
  };

  const handlePickKey = (e) => {
    e.stopPropagation();
    if (BACKK(e)) { e.preventDefault(); setPicking(null); return; }
        const key = keyOf(pickRefs.current, e.target);
    // Typing happens INSIDE the real input (reached only via OK on the
    // "custom" button, which opens the TV keyboard). While the input has
    // focus, let everything through except Enter — Enter commits: blur the
    // input (closes the keyboard) and land on Save.
    if (e.target === inputRef.current) {
      if (e.key === "Enter") {
        e.preventDefault();
        inputRef.current?.blur();
        setTimeout(() => focusPick("save"), 60);
      }
      return;
    }
    if (OKK(e)) {
      e.preventDefault();
      if (key === "custom") { inputRef.current?.focus(); return; }
      e.target?.click?.();
      return;
    }
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
    if (!key) { e.preventDefault(); focusPick("m0"); return; }
    const [r, c] = PICK_POS[key];
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (c > 0) focusPick(PICK_ROWS[r][c - 1]);
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      if (c < PICK_ROWS[r].length - 1) focusPick(PICK_ROWS[r][c + 1]);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (r > 0) { const up = PICK_ROWS[r - 1]; focusPick(up[Math.min(c, up.length - 1)]); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (r < PICK_ROWS.length - 1) { const dn = PICK_ROWS[r + 1]; focusPick(dn[Math.min(c, dn.length - 1)]); }
      return;
    }
  };

  const handlePcModalKey = (e) => {
    e.stopPropagation();
    if (e.key === "ArrowLeft") { e.preventDefault(); pcNoRef.current?.focus(); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); pcYesRef.current?.focus(); return; }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") { e.preventDefault(); return; }
    if (OKK(e)) {
      e.preventDefault();
      e.target === pcNoRef.current ? cancelTakeControl() : confirmTakeControl();
      return;
    }
    if (BACKK(e)) { e.preventDefault(); cancelTakeControl(); return; }
  };

  // Dead-remote recapture (timer pattern).
  const handleBlur = (e) => {
    if (e.relatedTarget) return;
    setTimeout(() => {
      if (document.activeElement === document.body && !picking && !pendingControl) {
        if (sending) ovCancelRef.current?.focus();
        else focusKey(windingDown ? "edit" : "t0");
      }
    }, 0);
  };

  return (
    <div className="ixat-alerts" onKeyDown={handleKey} onBlur={handleBlur}>
      <style>{IXAT_STYLE}</style>
      <div className="ixat-panel">
        <div className="ixat-tag">ALERTS</div>
        <div className="ixat-head">
          <div>
            <h1 className="ixat-title">One-Tap Band Alerts</h1>
            <div className="ixat-tagline">
              {editMode
                ? "Select a color to change its label"
                : "Select a color to flash & buzz everyone's bands!"}
            </div>
          </div>
          <button ref={setBtnRef("edit")} tabIndex={0}
            className={`ixat-edit-btn ${editMode ? "on" : ""}`}
            onClick={() => setEditMode((v) => !v)}>
            {editMode ? "DONE" : "EDIT LABELS"}
          </button>
        </div>
        <div className="ixat-grid">
          {ALERT_COLORS.map((c, i) => (
            <button key={c.key} ref={setBtnRef(`t${i}`)} tabIndex={windingDown ? -1 : 0}
              className="ixat-tile"
              style={{ background: c.hex, ...(windingDown ? { filter: "grayscale(.85) brightness(.55)", cursor: "default" } : {}) }}
              onClick={() => requestSend(c.key)} disabled={!!sending || windingDown}>
              {editMode && <span className="ixat-tile-pencil">✎</span>}
              <span className="ixat-tile-label">{meanings[c.key]}</span>
            </button>
          ))}
        </div>
        <div className="ixat-actions">
          <button ref={setBtnRef("back")} tabIndex={0} className="ixat-btn-back" onClick={onBack}>Back</button>
        </div>

        {sending && (
          <div className="ixat-ov">
            <div className="ixat-ov-label">{sending.label}</div>
            {ovPhase === "waking" && (
              <>
                <div className="ixat-ov-count">{countLeft}</div>
                <div className="ixat-ov-phase">Sending Alert In...</div>
              </>
            )}
            {ovPhase === "circle" && (
              <>
                <div className="ixat-ov-ring" style={{ color: sending.hex }} />
                <div className="ixat-ov-phase">Sending your alert...</div>
              </>
            )}
            {ovPhase === "lightning" && (
              <>
                <div className="ixat-ov-flash" style={{ color: sending.hex }} />
                <div className="ixat-ov-phase">Flashing &amp; buzzing!</div>
              </>
            )}
            {ovPhase === "done" ? (
              <div className="ixat-ov-done">Alert sent!</div>
            ) : (
              <button ref={ovCancelRef} tabIndex={0} className="ixat-ov-cancel"
                onClick={abortSequence}>Cancel</button>
            )}
          </div>
        )}
      </div>

      {picking && (
        <div className="ixat-pick-back" onKeyDown={handlePickKey}>
          <div className="ixat-pick">
            <h3>This Color Means...</h3>
            <p className="ixat-pick-sub">Pick a label or create your own</p>
            <div className="ixat-pick-list">
              {PRESET_MEANINGS.map((m, i) => (
                <button key={m} ref={setPickRef(`m${i}`)} tabIndex={0}
                  className={`ixat-pick-item ${pickSel === m && !pickCustom ? "on" : ""}`}
                  onClick={() => { setPickSel(m); setPickCustom(""); }}>
                  {m}
                </button>
              ))}
            </div>
                        <div className="ixat-pick-custom">
              <button ref={setPickRef("custom")} tabIndex={0} className="ixat-pick-item ixat-pick-customBtn"
                onClick={() => inputRef.current?.focus()}>
                {pickCustom ? "Edit Your Text" : "Type Your Own..."}
              </button>
              <input ref={inputRef} tabIndex={-1}
                className="ixat-pick-input" type="text" maxLength={24}
                placeholder="Your label..."
                value={pickCustom}
                onChange={(e) => setPickCustom(e.target.value)}
                onBlur={() => setTimeout(() => {
                  // Keyboard's own OK/Done blurs without keydown — still land on Save.
                  if (document.activeElement === document.body) focusPick("save");
                }, 0)} />
            </div>
            <div className="ixat-pick-btns">
              <button ref={setPickRef("cancel")} tabIndex={0} className="ixat-pick-cancel"
                onClick={() => setPicking(null)}>Cancel</button>
              <button ref={setPickRef("save")} tabIndex={0} className="ixat-pick-save"
                onClick={savePick}>Save</button>
            </div>
          </div>
        </div>
      )}

      {pendingControl && hasHolder && holder?.holderDeviceId !== device?._id && (
        <div className="ixat-modal-back" onKeyDown={handlePcModalKey}>
          <div className="ixat-modal">
            <h3>Take Control of Your Magic Beacon?</h3>
            <p>
              Your Wait Time {holder.holderDeviceType}: {holder.holderLabel} is
              currently controlling the beacon. Alerts need control to light
              your bands. Switch control to this device?
            </p>
            <div className="ixat-modal-btns">
              <button className="ixat-modal-btn ixat-modal-no" ref={pcNoRef} tabIndex={0}
                onClick={cancelTakeControl}>No</button>
              <button className="ixat-modal-btn ixat-modal-yes" ref={pcYesRef} tabIndex={0}
                onClick={confirmTakeControl}>Yes, Switch</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractAlertsTV;
